// Full-document render of the unified sales-channel shipment-status page
// (step 3): the three-level breadcrumbs, an order-detail tracker island per
// returned order, and the source-appropriate CMS tracking blocks (the server
// branches tracking_page_blocks vs tracking_page_blocks_trs on the order source).
import { Immutable } from "immer";
import React from "react";
import { FAQsBlock } from "tsi-common-react/src/components/FAQsBlock/index";
import { ProductCalloutBlock } from "tsi-common-react/src/components/ProductCalloutBlock/index";

import {
    SharedFAQsBlock as FAQsBlockValue,
    ProductCalloutBlock as ProductCalloutBlockValue,
    templates,
} from "@reactivated";

import { PageSkeleton } from "../components/PageSkeleton";
import { Template as TrackOrderDetails } from "./TrackOrderDetailsTemplate";

// tracking_page_blocks(_trs) is an inline Wagtail StreamField (not a thelabui
// client-renderable field), so it cannot be Pick'd through reactivated. The
// server serializes each block to a {type, id, value} row and the wrapper
// dispatches on type.
type TrackingBlockRow =
    | {
          readonly type: "faqs";
          readonly id: string;
          readonly value: FAQsBlockValue;
      }
    | {
          readonly type: "product_callout";
          readonly id: string;
          readonly value: ProductCalloutBlockValue;
      };

const renderTrackingBlock = (
    block: Immutable<TrackingBlockRow>,
): React.ReactNode => {
    switch (block.type) {
        case "faqs":
            return <FAQsBlock key={block.id} value={block.value} />;
        case "product_callout":
            return <ProductCalloutBlock key={block.id} value={block.value} />;
    }
};

export const Template = (
    props: templates.UnifiedOrderShipmentStatusDocumentTemplate,
): React.JSX.Element => {
    const trackingBlocks: readonly Immutable<TrackingBlockRow>[] =
        props.tracking_blocks_json
            ? (JSON.parse(props.tracking_blocks_json) as Immutable<
                  TrackingBlockRow[]
              >)
            : [];
    return (
        <PageSkeleton pageChrome={props.page_chrome}>
            <div className="l-capped-width">
                <section>
                    <div>
                        <nav className="unified-order-breadcrumbs">
                            {props.crumbs.map((crumb, i) => (
                                <React.Fragment key={i}>
                                    {i > 0 && " / "}
                                    {crumb.href ? (
                                        <a href={crumb.href}>{crumb.label}</a>
                                    ) : (
                                        <span>{crumb.label}</span>
                                    )}
                                </React.Fragment>
                            ))}
                        </nav>
                        <div>
                            {props.order_details.map((detail, i) => (
                                <TrackOrderDetails key={i} {...detail} />
                            ))}
                        </div>
                    </div>
                </section>
            </div>
            <section className="track-shipment-blocks">
                {trackingBlocks.map((block) => renderTrackingBlock(block))}
            </section>
        </PageSkeleton>
    );
};
